import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "../lib/api";
import { usePdfPreview } from "../components/PdfPreview";
import { useConfig } from "../lib/useConfig";
import { useLang } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { PageHeader, pct } from "../lib/ui";
import { isoWeek, weekOptions } from "../lib/week";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Printer, TrendingUp, TrendingDown, FileDown } from "lucide-react";

function downloadWeeklyPdf(params) {
  const token = localStorage.getItem("token");
  const qs = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v))).toString();
  fetch(`${process.env.REACT_APP_BACKEND_URL}/api/weekly-report/pdf?${qs}`, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => r.blob()).then((b) => {
      const url = URL.createObjectURL(b);
      const a = document.createElement("a");
      a.href = url; a.download = `Rapport_S${String(params.week).padStart(2, "0")}_${params.year}.pdf`; a.click();
      URL.revokeObjectURL(url);
    });
}

const Sec = ({ n, title, children }) => {
  const { t } = useLang();
  return (
    <div className="mb-6">
      <h3 className="text-base font-semibold text-slate-900 mb-2 flex items-center gap-2">
        <span className="text-[#F05A28]">{n}.</span> {t(title)}
      </h3>
      <div className="text-sm text-slate-600 leading-relaxed">{children}</div>
    </div>
  );
};

export default function WeeklyReport() {
  const { data: cfg } = useConfig();
  const { t, lang } = useLang();
  const { user } = useAuth();
  const cur = isoWeek();
  const [scope, setScope] = useState("national");
  const [province, setProvince] = useState("");
  const [zone, setZone] = useState("");
  const [week, setWeek] = useState(cur.week);
  const [year, setYear] = useState(cur.year);
  const pdf = usePdfPreview();

  const geo = cfg?.geo || {};
  const zones = province ? Object.values(geo[province]?.territories || {}).flat() : [];

  const params = { year, week, scope, province: province || undefined, health_zone: zone || undefined, lang };
  const { data, isLoading } = useQuery({
    queryKey: ["weekly", params],
    queryFn: async () => (await api.get("/weekly-report", { params })).data,
    enabled: !!(scope === "national" || (scope === "province" && province) || (scope === "zone" && zone)),
  });

  const Change = ({ v }) => v == null ? <span className="text-slate-400">-</span> :
    <span className={`inline-flex items-center gap-0.5 font-medium ${v > 0 ? "text-emerald-600" : v < 0 ? "text-red-600" : "text-slate-500"}`}>
      {v > 0 ? <TrendingUp className="h-3 w-3" /> : v < 0 ? <TrendingDown className="h-3 w-3" /> : null}{v > 0 ? "+" : ""}{v}%
    </span>;

  return (
    <div data-testid="weekly-page">
      <PageHeader title={t("Générateur de Rapport Hebdomadaire")} subtitle={t("Narratif automatique à partir des rapports quotidiens")}>
        <Button size="sm" className="bg-[#F05A28] hover:bg-[#D84A1B]" data-testid="download-pdf" onClick={() => downloadWeeklyPdf({ year, week, scope, province, health_zone: zone })}><FileDown className="h-4 w-4 mr-1" /> {t("Télécharger PDF")}</Button>
        <Button variant="outline" size="sm" data-testid="print-weekly" onClick={() => pdf.show(
          () => api.get("/weekly-report/pdf", { responseType: "blob", params: Object.fromEntries(Object.entries({ year, week, scope, province, health_zone: zone }).filter(([, v]) => v)) }).then((x) => x.data),
          { title: `${t("Rapport hebdomadaire")} S${String(week).padStart(2, "0")} ${year}`, filename: `Rapport_S${String(week).padStart(2, "0")}_${year}.pdf` },
        ).catch(() => toast.error(t("PDF indisponible.")))}><Printer className="h-4 w-4 mr-1" /> {t("Aperçu / Imprimer")}</Button>
      </PageHeader>

      <div className="bg-white border border-slate-200 rounded-md p-4 mb-6 shadow-sm grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
        <div><Label className="text-xs">{t("Niveau")}</Label>
          <Select value={scope} onValueChange={(v) => { setScope(v); setProvince(""); setZone(""); }}>
            <SelectTrigger data-testid="weekly-scope"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="national">{t("National / Programme")}</SelectItem>
              <SelectItem value="province">{t("Province")}</SelectItem>
              <SelectItem value="zone">{t("Zone de Santé")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {scope !== "national" && (
          <div><Label className="text-xs">{t("Province")}</Label>
            <Select value={province} onValueChange={(v) => { setProvince(v); setZone(""); }}>
              <SelectTrigger data-testid="weekly-province"><SelectValue placeholder={t("Toutes")} /></SelectTrigger>
              <SelectContent>{Object.keys(geo).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        {scope === "zone" && (
          <div><Label className="text-xs">{t("Zone de Santé")}</Label>
            <Select value={zone} onValueChange={setZone} disabled={!province}>
              <SelectTrigger data-testid="weekly-zone"><SelectValue placeholder={t("Toutes")} /></SelectTrigger>
              <SelectContent>{zones.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        <div><Label className="text-xs">{t("Semaine")}</Label>
          <Select value={String(week)} onValueChange={(v) => setWeek(Number(v))}>
            <SelectTrigger data-testid="weekly-week"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-60">{weekOptions().map((w) => <SelectItem key={w} value={String(w)}>S{String(w).padStart(2, "0")}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">{t("Année")}</Label>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger data-testid="weekly-year"><SelectValue /></SelectTrigger>
            <SelectContent>{[cur.year, cur.year - 1].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-4 mb-6 shadow-sm" data-testid="targeted-exports">
        <p className="text-sm font-semibold text-slate-800 mb-1">{t("Exports ciblés")}</p>
        <p className="text-xs text-slate-500 mb-3">{t("Téléchargez le rapport hebdomadaire par Province ou par Zone de Santé en un clic.")}</p>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => downloadWeeklyPdf({ year, week, scope: "national", lang })} data-testid="export-national"><FileDown className="h-4 w-4 mr-1" /> {t("Rapport national")}</Button>
          {user?.province && (
            <Button variant="outline" size="sm" onClick={() => downloadWeeklyPdf({ year, week, scope: "province", province: user.province, lang })} data-testid="export-my-province"><FileDown className="h-4 w-4 mr-1" /> {t("Mon rapport (ma province)")} · {user.province}</Button>
          )}
          {province && (
            <Button variant="outline" size="sm" onClick={() => downloadWeeklyPdf({ year, week, scope: "province", province, lang })} data-testid="export-province"><FileDown className="h-4 w-4 mr-1" /> PDF {province}</Button>
          )}
          {province && zones.map((z) => (
            <Button key={z} variant="ghost" size="sm" className="text-slate-600 border border-slate-200" onClick={() => downloadWeeklyPdf({ year, week, scope: "zone", province, health_zone: z, lang })} data-testid={`export-zone-${z}`}>{z}</Button>
          ))}
          {province && zones.length > 0 && (
            <Button size="sm" className="bg-[#0284C7] hover:bg-[#0369A1]" onClick={() => zones.forEach((z, i) => setTimeout(() => downloadWeeklyPdf({ year, week, scope: "zone", province, health_zone: z, lang }), i * 500))} data-testid="export-all-zones"><FileDown className="h-4 w-4 mr-1" /> {t("Télécharger toutes les zones")}</Button>
          )}
        </div>
      </div>

      {isLoading ? <p className="text-slate-400 text-sm">{t("Génération…")}</p> : data && (
        <div className="bg-white border border-slate-200 rounded-md p-6 lg:p-8 shadow-sm" data-testid="weekly-content">
          <div className="border-b border-slate-200 pb-4 mb-6">
            <h2 className="text-xl font-bold text-slate-900">{t("Rapport de Surveillance")} · {data.scope_label}</h2>
            <p className="text-sm text-slate-500">{t("Semaine Épidémiologique")} S{String(data.week).padStart(2, "0")}/{data.year} · {data.period.from} {t("au")} {data.period.to}</p>
          </div>

          <Sec n="1" title="Aperçu de la notification"><p>{data.overview}</p></Sec>

          <Sec n="2" title="Activités menées">
            <div className="flex flex-wrap gap-2">
              {data.activities_summary.map((a) => <span key={a.name} className="bg-slate-100 px-2.5 py-1 rounded text-xs">{a.name}: <b>{a.count}</b></span>)}
            </div>
          </Sec>

          <Sec n="3" title="Indicateurs clés de surveillance">
            <Table>
              <TableHeader><TableRow className="bg-slate-50"><TableHead>{t("Indicateur")}</TableHead><TableHead className="text-right">{t("Semaine actuelle")}</TableHead><TableHead className="text-right">{t("Semaine précédente")}</TableHead><TableHead className="text-right">{t("Évolution")}</TableHead></TableRow></TableHeader>
              <TableBody>{data.indicators.map((r) => (
                <TableRow key={r.indicator}><TableCell className="font-medium">{r.indicator}</TableCell>
                  <TableCell className="text-right">{r.current ?? "N/A"}</TableCell>
                  <TableCell className="text-right text-slate-500">{r.previous ?? "N/A"}</TableCell>
                  <TableCell className="text-right"><Change v={r.change} /></TableCell></TableRow>
              ))}</TableBody>
            </Table>
          </Sec>

          <Sec n="4" title="Constats clés">
            {data.findings_summary.length === 0 ? <p className="text-slate-400">{t("Aucun constat notable.")}</p> :
              data.findings_summary.map((f) => <p key={f.category}>• <b>{f.category}</b> ({f.count}) {f.examples.length > 0 && <span className="text-slate-500">- {f.examples.join("; ")}</span>}</p>)}
          </Sec>

          <Sec n="5" title="Défis">
            {data.challenges_summary.length === 0 ? <p className="text-slate-400">{t("Aucun défi signalé.")}</p> :
              data.challenges_summary.slice(0, 8).map((c) => <p key={c.category}>• <b>{c.category}</b> · {t("signalé")} {c.count} {t("fois")} · {t("zones")}: {c.zones.join(", ")} · {t("gravité")}: {c.severity.map((s) => t(s)).join(", ")}</p>)}
          </Sec>

          <Sec n="6" title="Recommandations">
            {data.high_priority_unresolved.length === 0 ? <p className="text-slate-400">{t("Aucune recommandation prioritaire non résolue.")}</p> :
              data.high_priority_unresolved.map((r) => <p key={r.id}>• <span className="text-red-600 font-semibold">[{t(r.priority)}]</span> {r.recommendation} <span className="text-slate-400">({r.health_zone})</span></p>)}
          </Sec>

          <Sec n="7" title="Réalisations / Impact">
            {data.achievements.length === 0 ? <p className="text-slate-400">{t("Aucune réalisation rapportée.")}</p> :
              data.achievements.map((a, i) => <p key={i}>• {a}</p>)}
          </Sec>

          <Sec n="8" title="Comparaison avec la semaine précédente">
            {data.comparison.length === 0 ? <p className="text-slate-400">{t("Aucune variation significative (≥15%).")}</p> :
              data.comparison.map((c, i) => <p key={i}>• {c}</p>)}
          </Sec>

          <Sec n="9" title="Interprétation programmatique">
            <div className="bg-[#FFF3EE] border border-[#F05A28]/20 rounded-lg p-4 text-slate-700">{data.interpretation}</div>
          </Sec>
        </div>
      )}
      {pdf.el}
    </div>
  );
}
